<template>
  <Teleport to="body">
    <div
      id="contact-us-modal"
      :class="[
        'animate__slideInUp animate__animated',
        'tw-w-full tw-h-[100vh] tw-bg-[#FAFAFA] ',
        // 'dark:tw-bg-black',
        'tw-fixed tw-left-0 tw-top-0 tw-z-50 tw-flex tw-flex-col tw-items-center',
        'tw-pl-[24px] apTablet:tw-pl-[64px] tw-pt-[64px]',
        'tw-pr-[24px] apTablet:tw-pr-[64px] tw-z-[999]',
      ]"
    >
      <div class="tw-flex tw-justify-end tw-w-full">
        <img
          class="hover:tw-opacity-50 tw-cursor-pointer tw-h-10 tw-w-10"
          :src="$cdn('icons/contact-us/icon_close_dark.svg')"
          alt="close_icon"
          @click="onClickCloseButton"
        >
      </div>
      <div
        :class="[
          'tw-pt-[64px] tw-flex tw-flex-col tw-max-w-[1024px] tw-w-full',
          'tw-h-[calc(100vh-104px)]',
          { 'tw-hidden': activeIndex === 6 }
        ]"
      >
        <ProgressBar :total-index="5" :active-index="activeIndex" @update:active-index="onPageChange" />

        <ContactUsPage_1 v-if="activeIndex === 1" :contact-us="contactUs" @click-next="activeIndex = 2" />
        <ContactUsPage_2 v-if="activeIndex === 2" :contact-us="contactUs" @click-next="activeIndex = 3" />
        <ContactUsPage_3 v-if="activeIndex === 3" :contact-us="contactUs" @click-next="activeIndex = 4" />
        <ContactUsPage_4 v-if="activeIndex === 4" :contact-us="contactUs" @click-next="activeIndex = 5" />
        <ContactUsPage_5
          v-if="activeIndex === 5"
          :contact-us="contactUs"
          :is-loading="isSendingEmail"
          @click-next="onClickSubmit"
          @recaptcha="recaptchaToken = $event"
        />
      </div>
      <ContactUsPage_6 v-if="activeIndex === 6" @click-our-work="onClickOurWork" />
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import ContactUsPage_1 from './ContactUsPage_1.vue';
import ContactUsPage_2 from './ContactUsPage_2.vue';
import ContactUsPage_3 from './ContactUsPage_3.vue';
import ContactUsPage_4 from './ContactUsPage_4.vue';
import ContactUsPage_5 from './ContactUsPage_5.vue';
import ContactUsPage_6 from './ContactUsPage_6.vue';

import { ContactUs } from '../../model/interfaces/ContactUs';

import { getDarkMode } from '../../support/helper';

const contactUs = reactive(ContactUs.create());
const recaptchaToken = ref<string | null>(null);
const isSendingEmail = ref(false);

const isDarkMode = ref(false);

const props = defineProps({
  contactUs: {
    type: ContactUs,
    required: false,
    default: null
  },
  isPage: {
    type: Boolean,
    default: false
  }
});

onMounted(() => {
  isDarkMode.value = getDarkMode();
  if (props.contactUs) {
    Object.assign(contactUs, props.contactUs);
  }

  window.history.pushState({}, '', '/contact-us');

  lockScroll();
});

onBeforeUnmount(() => {
  unlockScroll();
  window.history.replaceState({}, '', '/');
});


import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import ProgressBar from './ProgressBar.vue';

const activeIndex = ref(1);
const title = computed(() => {
  switch (activeIndex.value) {
    case 1:
      return 'Tell us about yourself';
    case 2:
      return 'How can we help you?';
    case 3:
      return 'Select the project components that apply';
    case 4:
      return 'What is the budget for your project?';
    case 5:
      return 'Describe your project in detail';
    default:
      return 'Tell us about yourself';
  }
});

// Lock scroll when modal is active
const lockScroll = () => {
  document.body.style.overflow = 'hidden';
};

// Unlock scroll when modal is inactive
const unlockScroll = () => {
  document.body.style.overflow = '';
};

const emit = defineEmits(['closeForm']);

function onClickCloseButton() {
  if (props.isPage) {
    // back to homepage
    window.location.href = '/';
  }
  const container = document.getElementById('contact-us-modal');
  if (container) {
    container.classList.remove('animate__slideInUp');
    container.classList.add('animate__slideOutDown');
    setTimeout(() => {
      container.classList.remove('animate__slideOutDown');
      emit('closeForm');
    }, 300);
  }
}

function onClickOurWork() {
  window.location.href = '#our-work';
  onClickCloseButton();
}

function onPageChange(event: any) {
  activeIndex.value = event;
}

function onClickSubmit() {
  isSendingEmail.value = true;
  contactUs.sendEmailWithAttachments(recaptchaToken.value).then(() => {
    activeIndex.value = 6;
  }).catch((error) => {
    alert(error);
  }).finally(() => {
    isSendingEmail.value = false;
  });
}
</script>

<style scoped>

#contact-us-modal {
  --animate-duration: 300ms;
}
</style>